import { dataService } from '../apis/dataService';
import type { BalanceSheet, FinancialDataQueryParams } from '../../types/api';
import type { CategoryData } from '../../components/charts/WaterfallChart';
import balanceSheetLayout from '../../financialLayouts/balanceSheetLayout.json';

interface LayoutCategory {
  properties: Array<{
    category_code: number;
    parent_code: number;
    has_children: number;
    highlight: number;
  }>;
  children?: Record<string, LayoutCategory>;
}

interface BalanceSheetLayout {
  [key: string]: LayoutCategory;
}

/**
 * Extract LIABILITIES & EQUITY categories from the layout JSON
 * @returns Array of category objects with name and category_code
 */
function extractLiabilitiesEquityCategories(): Array<{ name: string; category_code: number }> {
  const layout = balanceSheetLayout as BalanceSheetLayout;
  const liabilitiesEquity = layout['LIABILITIES & EQUITY'];
  
  if (!liabilitiesEquity || !liabilitiesEquity.children) {
    return [];
  }

  const categories: Array<{ name: string; category_code: number }> = [];
  
  // Extract each child category
  Object.keys(liabilitiesEquity.children).forEach((categoryName) => {
    const category = liabilitiesEquity.children![categoryName];
    if (category.properties && category.properties.length > 0) {
      const categoryCode = category.properties[0].category_code;
      categories.push({
        name: categoryName,
        category_code: categoryCode
      });
    }
  });

  return categories;
}

/**
 * Extract ASSETS categories from the layout JSON
 * @returns Array of category objects with name and category_code
 */
function extractAssetsCategories(): Array<{ name: string; category_code: number }> {
  const layout = balanceSheetLayout as BalanceSheetLayout;
  const assets = layout['ASSETS'];
  
  if (!assets || !assets.children) {
    return [];
  }

  const categories: Array<{ name: string; category_code: number }> = [];
  
  // Extract each child category
  Object.keys(assets.children).forEach((categoryName) => {
    const category = assets.children![categoryName];
    if (category.properties && category.properties.length > 0) {
      const categoryCode = category.properties[0].category_code;
      categories.push({
        name: categoryName,
        category_code: categoryCode
      });
    }
  });

  return categories;
}

/**
 * Fetch balance sheet data and aggregate values by category
 * @param user_id_no - The user ID number
 * @param unit_id_no - The unit ID number
 * @returns Promise<BalanceSheet[]> - Balance sheet data
 */
async function fetchBalanceSheetData(
  user_id_no: number,
  unit_id_no: number
): Promise<BalanceSheet[]> {
  if (!user_id_no || !unit_id_no) {
    return [];
  }

  try {
    const params: FinancialDataQueryParams = {
      user_id_no,
      unit_id_no
    };

    return await dataService.getBalanceSheet(params);
  } catch (error) {
    console.error(`Error fetching balance sheet for unit ${unit_id_no}:`, error);
    return [];
  }
}

/**
 * Parse date string and get the latest date
 * @param dateStr - Date string in YYYYMM format or standard date format
 * @returns Date object or null if invalid
 */
function parseDate(dateStr: string): Date | null {
  if (!dateStr) {
    return null;
  }

  // Handle YYYYMM format (e.g., "202401")
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    return new Date(year, month, 1);
  }

  // Handle standard date formats
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Get the maximum date from balance sheet data
 * @param balanceSheetData - Array of balance sheet data
 * @returns The maximum date string, or null if no data
 */
function getMaxDate(balanceSheetData: BalanceSheet[]): string | null {
  if (balanceSheetData.length === 0) {
    return null;
  }

  let maxDate: Date | null = null;
  let maxDateStr: string | null = null;

  balanceSheetData.forEach(item => {
    const date = parseDate(item.calendar_date);
    if (date && (!maxDate || date > maxDate)) {
      maxDate = date;
      maxDateStr = item.calendar_date;
    }
  });

  return maxDateStr;
}

/**
 * Get balance sheet values for each category from a specific date
 * @param balanceSheetData - Array of balance sheet data
 * @param categoryCodes - Array of category codes to filter by
 * @param targetDate - The date to use for all categories
 * @returns Map of category_code to value from the target date
 */
function getCategoryValuesForDate(
  balanceSheetData: BalanceSheet[],
  categoryCodes: number[],
  targetDate: string
): Map<number, number> {
  const categoryValues = new Map<number, number>();
  
  // Filter data by category codes and target date
  const filteredData = balanceSheetData.filter(item => 
    categoryCodes.includes(item.category_code) && 
    item.calendar_date === targetDate
  );

  if (filteredData.length === 0) {
    return categoryValues;
  }

  // Group by category_code and sum values for the same date
  filteredData.forEach(item => {
    const currentValue = categoryValues.get(item.category_code) || 0;
    categoryValues.set(item.category_code, currentValue + (item.category_value || 0));
  });

  return categoryValues;
}

/**
 * Fetch and calculate liabilities and equity data for hierarchical bar chart
 * @param user_id_no - The user ID number
 * @param unit_id_no - The unit ID number
 * @returns Promise<CategoryData[]> - Array of category data for the chart
 */
export async function fetchLiabilitiesEquityData(
  user_id_no: number,
  unit_id_no: number
): Promise<CategoryData[]> {
  if (!user_id_no || !unit_id_no) {
    return [];
  }

  try {
    // Extract categories from layout
    const categories = extractLiabilitiesEquityCategories();
    
    if (categories.length === 0) {
      console.warn('No LIABILITIES & EQUITY categories found in layout');
      return [];
    }

    // Get category codes
    const categoryCodes = categories.map(cat => cat.category_code);

    // Fetch balance sheet data
    const balanceSheetData = await fetchBalanceSheetData(user_id_no, unit_id_no);

    if (balanceSheetData.length === 0) {
      // Return empty data with category names
      return categories.map(cat => ({
        name: cat.name,
        value: 0
      }));
    }

    // Find the maximum date from all balance sheet data
    const maxDate = getMaxDate(balanceSheetData);
    
    if (!maxDate) {
      // Return empty data with category names if no valid date found
      return categories.map(cat => ({
        name: cat.name,
        value: 0
      }));
    }

    // Get values for all categories from the same max date
    const categoryValues = getCategoryValuesForDate(balanceSheetData, categoryCodes, maxDate);

    // Build the result array (excluding "Total" if it exists in categories)
    const result: CategoryData[] = categories
      .filter(cat => cat.name.toLowerCase() !== 'total') // Exclude "Total" from categories
      .map(cat => {
        const value = categoryValues.get(cat.category_code) || 0;
        return {
          name: cat.name,
          value: value
        };
      });

    // Calculate Total as the sum of all other categories
    const totalValue = result.reduce((sum, item) => sum + item.value, 0);

    // Check if Total already exists in categories, if not add it
    const hasTotal = result.some(item => item.name.toLowerCase() === 'total');
    if (!hasTotal) {
      result.push({
        name: 'Total',
        value: totalValue
      });
    } else {
      // Update existing Total
      const totalIndex = result.findIndex(item => item.name.toLowerCase() === 'total');
      if (totalIndex !== -1) {
        result[totalIndex].value = totalValue;
      }
    }

    return result;
  } catch (error) {
    console.error('Error fetching liabilities and equity data:', error);
    return [];
  }
}

/**
 * Fetch and calculate assets data for waterfall chart
 * @param user_id_no - The user ID number
 * @param unit_id_no - The unit ID number
 * @returns Promise<CategoryData[]> - Array of category data for the chart
 */
export async function fetchAssetsData(
  user_id_no: number,
  unit_id_no: number
): Promise<CategoryData[]> {
  if (!user_id_no || !unit_id_no) {
    return [];
  }

  try {
    // Extract categories from layout
    const categories = extractAssetsCategories();
    
    if (categories.length === 0) {
      console.warn('No ASSETS categories found in layout');
      return [];
    }

    // Get category codes
    const categoryCodes = categories.map(cat => cat.category_code);

    // Fetch balance sheet data
    const balanceSheetData = await fetchBalanceSheetData(user_id_no, unit_id_no);

    if (balanceSheetData.length === 0) {
      // Return empty data with category names
      return categories.map(cat => ({
        name: cat.name,
        value: 0
      }));
    }

    // Find the maximum date from all balance sheet data
    const maxDate = getMaxDate(balanceSheetData);
    
    if (!maxDate) {
      // Return empty data with category names if no valid date found
      return categories.map(cat => ({
        name: cat.name,
        value: 0
      }));
    }

    // Get values for all categories from the same max date
    const categoryValues = getCategoryValuesForDate(balanceSheetData, categoryCodes, maxDate);

    // Build the result array (excluding "Total" if it exists in categories)
    const result: CategoryData[] = categories
      .filter(cat => cat.name.toLowerCase() !== 'total') // Exclude "Total" from categories
      .map(cat => {
        const value = categoryValues.get(cat.category_code) || 0;
        return {
          name: cat.name,
          value: value
        };
      });

    // Calculate Total as the sum of all other categories
    const totalValue = result.reduce((sum, item) => sum + item.value, 0);

    // Check if Total already exists in categories, if not add it
    const hasTotal = result.some(item => item.name.toLowerCase() === 'total');
    if (!hasTotal) {
      result.push({
        name: 'Total',
        value: totalValue
      });
    } else {
      // Update existing Total
      const totalIndex = result.findIndex(item => item.name.toLowerCase() === 'total');
      if (totalIndex !== -1) {
        result[totalIndex].value = totalValue;
      }
    }

    return result;
  } catch (error) {
    console.error('Error fetching assets data:', error);
    return [];
  }
}

